/**
 * The checkout's two CTA labels (Chapter 10, F23).
 *
 * P3b (F10/F11) collapsed three competing CTAs into one path — "Review request"
 * opens the itemised review, and the button inside it sends — but it could not
 * touch `packages/`, so both strings stayed hardcoded and the surface's
 * `submitLabel` was left rendering nowhere. Both are authored now.
 *
 * The fallbacks matter more than they look: every `checkout-labels` document in
 * Cosmos predates these fields, so until the surface is re-seeded the defaults
 * below ARE what a customer sees. They are therefore the exact strings P3b
 * shipped, not new copy.
 *
 * `submitLabel` is deliberately NOT consulted. It reads "Get Free Proposal" in
 * the live document — neither of these buttons — so reusing it would put stale
 * copy back on a customer-facing CTA. See the schema's `@deprecated` note.
 */

export const DEFAULT_REVIEW_LABEL = 'Review request';
export const DEFAULT_SEND_LABEL = 'Send request';

/** Just the two fields this resolver reads. */
export interface CtaLabelSource {
  readonly reviewLabel?: string | undefined;
  readonly sendLabel?: string | undefined;
}

export interface CtaLabels {
  /** The page's one primary action. Opens the review; sends nothing. */
  readonly reviewLabel: string;
  /** The button inside the review that actually sends the order. */
  readonly sendLabel: string;
}

/**
 * Resolve both labels, treating a blank or whitespace-only authored value as
 * absent — an empty CTA is worse than the default one, and a surface edited down
 * to spaces is an accident rather than an instruction.
 */
export function resolveCtaLabels(source: CtaLabelSource): CtaLabels {
  return {
    reviewLabel: source.reviewLabel?.trim() || DEFAULT_REVIEW_LABEL,
    sendLabel: source.sendLabel?.trim() || DEFAULT_SEND_LABEL,
  };
}
